/*
 * Van Route Management Component
 *
 * Admin interface for managing shuttle van routes.
 * Features:
 * - Interactive map for drawing routes by clicking points
 * - Draggable route point markers for easy editing
 * - Route listing with color-coded visualization
 * - Create, edit, delete, and toggle active status
 * - Color picker for route visualization
 * - Real-time preview of route polylines
 */

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { MapContainer, TileLayer, Polyline, useMapEvents, Marker, Popup, useMap } from 'react-leaflet';
import axios from 'axios';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { API } from '../../utils/config';

// Draggable Marker Component for route points
const DraggableRouteMarker = ({ position, index, total, color, onDragEnd, onDelete, isEditMode }) => {
  const markerRef = useRef(null);
  const isFirst = index === 0;
  const isLast = index === total - 1;
  const pointColor = isFirst ? '#10B981' : isLast ? '#EF4444' : color || '#3B82F6';

  const icon = useMemo(() => L.divIcon({
    html: `<div style="
      background-color: ${pointColor};
      width: ${isEditMode ? '16px' : '12px'};
      height: ${isEditMode ? '16px' : '12px'};
      border-radius: 50%;
      border: 2px solid white;
      box-shadow: 0 2px 4px rgba(0,0,0,0.3);
      cursor: ${isEditMode ? 'move' : 'pointer'};
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 9px;
      font-weight: bold;
      color: white;
    ">${isEditMode ? index + 1 : ''}</div>`,
    className: '',
    iconSize: [isEditMode ? 16 : 12, isEditMode ? 16 : 12],
    iconAnchor: [isEditMode ? 8 : 6, isEditMode ? 8 : 6]
  }), [pointColor, isEditMode, index]);

  const eventHandlers = useMemo(() => ({
    dragend() {
      const marker = markerRef.current;
      if (marker != null) {
        const newPos = marker.getLatLng();
        onDragEnd(index, { lat: newPos.lat, lng: newPos.lng });
      }
    },
  }), [index, onDragEnd]);

  return (
    <Marker
      draggable={isEditMode}
      eventHandlers={eventHandlers}
      position={position}
      ref={markerRef}
      icon={icon}
    >
      <Popup>
        <div className="text-xs min-w-[150px]">
          <p className="font-medium text-sm mb-1">
            Point {index + 1}
            {isFirst && ' (Start)'}
            {isLast && ' (End)'}
          </p>
          <p className="text-gray-600 mb-2">
            {position[0].toFixed(5)}, {position[1].toFixed(5)}
          </p>
          {isEditMode && (
            <div className="flex space-x-1 pt-2 border-t border-gray-200">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(index);
                }}
                className="flex-1 px-2 py-1 bg-red-100 text-red-700 rounded text-xs hover:bg-red-200"
              >
                Delete Point
              </button>
            </div>
          )}
        </div>
      </Popup>
    </Marker>
  );
};

// Component to handle map clicks for inserting points
const InsertPointHandler = ({ coordinates, onInsertPoint, insertMode }) => {
  useMapEvents({
    click: (e) => {
      if (!insertMode || coordinates.length < 2) return;

      const clickLat = e.latlng.lat;
      const clickLng = e.latlng.lng;

      // Find the closest segment to insert the point
      let minDist = Infinity;
      let insertIndex = coordinates.length; // Default: append at end

      for (let i = 0; i < coordinates.length - 1; i++) {
        const p1 = coordinates[i];
        const p2 = coordinates[i + 1];

        // Calculate perpendicular distance to line segment
        const dist = pointToSegmentDistance(clickLat, clickLng, p1.lat, p1.lng, p2.lat, p2.lng);

        if (dist < minDist) {
          minDist = dist;
          insertIndex = i + 1;
        }
      }

      // Only insert if click is reasonably close to a segment (within ~500m)
      if (minDist < 0.005) {
        onInsertPoint(insertIndex, { lat: clickLat, lng: clickLng });
      } else {
        // If not near any segment, append at end
        onInsertPoint(coordinates.length, { lat: clickLat, lng: clickLng });
      }
    }
  });
  return null;
};

// Calculate distance from point to line segment
const pointToSegmentDistance = (px, py, x1, y1, x2, y2) => {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const lengthSq = dx * dx + dy * dy;

  if (lengthSq === 0) {
    return Math.sqrt((px - x1) ** 2 + (py - y1) ** 2);
  }

  let t = ((px - x1) * dx + (py - y1) * dy) / lengthSq;
  t = Math.max(0, Math.min(1, t));

  const nearestX = x1 + t * dx;
  const nearestY = y1 + t * dy;

  return Math.sqrt((px - nearestX) ** 2 + (py - nearestY) ** 2);
};

// Map bounds fitter component - only fits on initial load, not on every change
const MapBoundsFitter = ({ coordinates, fitOnce = false }) => {
  const map = useMap();
  const hasFitted = useRef(false);

  useEffect(() => {
    if (coordinates && coordinates.length > 0) {
      if (fitOnce && hasFitted.current) {
        // Don't re-fit if fitOnce is true and we've already fitted
        return;
      }
      const bounds = L.latLngBounds(coordinates.map(c => [c.lat, c.lng]));
      map.fitBounds(bounds, { padding: [50, 50] });
      hasFitted.current = true;
    }
  }, [coordinates, map, fitOnce]);

  // Reset when coordinates become empty (new route)
  useEffect(() => {
    if (!coordinates || coordinates.length === 0) {
      hasFitted.current = false;
    }
  }, [coordinates]);

  return null;
};

// Recalculates the Leaflet map size when its container changes (e.g. fullscreen toggle)
const MapResizeHandler = ({ trigger }) => {
  const map = useMap();
  useEffect(() => {
    const timer = setTimeout(() => map.invalidateSize(), 150);
    return () => clearTimeout(timer);
  }, [trigger, map]);
  return null;
};

// Map controller component for navigation - pans to specific point
const MapNavigator = ({ targetPoint, mapRef }) => {
  const map = useMap();

  // Store map reference for external control
  useEffect(() => {
    if (mapRef) {
      mapRef.current = map;
    }
  }, [map, mapRef]);

  // Pan to target point when it changes
  useEffect(() => {
    if (targetPoint) {
      map.setView([targetPoint.lat, targetPoint.lng], map.getZoom(), {
        animate: true,
        duration: 0.5
      });
    }
  }, [targetPoint, map]);

  return null;
};

// Calculate bearing between two points
const calculateBearing = (point1, point2) => {
  const lat1 = point1.lat * Math.PI / 180;
  const lat2 = point2.lat * Math.PI / 180;
  const dLng = (point2.lng - point1.lng) * Math.PI / 180;

  const y = Math.sin(dLng) * Math.cos(lat2);
  const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLng);

  const bearing = Math.atan2(y, x) * 180 / Math.PI;
  return (bearing + 360) % 360;
};

// Generate arrow markers along route (same as MapView.js)
const generateRouteArrows = (coordinates, color) => {
  if (!coordinates || coordinates.length < 2) return [];

  const arrows = [];
  const arrowInterval = Math.max(2, Math.floor(coordinates.length / 50)); // Double arrows - smaller interval

  for (let i = 0; i < coordinates.length - 1; i += arrowInterval) {
    const point1 = coordinates[i];
    const point2 = coordinates[i + 1];

    // Calculate midpoint
    const midLat = (point1.lat + point2.lat) / 2;
    const midLng = (point1.lng + point2.lng) / 2;

    // Calculate bearing/rotation
    const rotation = calculateBearing(point1, point2);

    arrows.push({
      position: [midLat, midLng],
      rotation: rotation,
      color: color
    });
  }

  return arrows;
};

// Create SVG arrow icon (same style as MapView.js)
const createRouteArrowIcon = (rotation, color) => {
  return L.divIcon({
    html: `
      <svg width="20" height="20" viewBox="0 0 20 20" style="transform: rotate(${rotation}deg);">
        <path
          d="M10 2 L14 10 L10 8 L6 10 Z"
          fill="${color}"
          stroke="white"
          stroke-width="1"
          opacity="0.9"
        />
      </svg>
    `,
    className: 'route-arrow-icon',
    iconSize: [20, 20],
    iconAnchor: [10, 10]
  });
};

// Direction arrow component for routes
const RouteDirectionArrows = ({ coordinates, color }) => {
  if (!coordinates || coordinates.length < 2) return null;

  const arrows = generateRouteArrows(coordinates, color);

  return (
    <>
      {arrows.map((arrow, idx) => (
        <Marker
          key={`arrow-${idx}`}
          position={arrow.position}
          icon={createRouteArrowIcon(arrow.rotation, arrow.color)}
        />
      ))}
    </>
  );
};

// Start/End markers for routes
const RouteEndpointMarkers = ({ coordinates, routeName }) => {
  if (!coordinates || coordinates.length < 2) return null;

  const startIcon = L.divIcon({
    html: `<div style="
      background-color: #10B981;
      width: 24px;
      height: 24px;
      border-radius: 50%;
      border: 3px solid white;
      box-shadow: 0 2px 6px rgba(0,0,0,0.4);
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;
      font-size: 12px;
      font-weight: bold;
    ">S</div>`,
    className: '',
    iconSize: [24, 24],
    iconAnchor: [12, 12]
  });

  const endIcon = L.divIcon({
    html: `<div style="
      background-color: #EF4444;
      width: 24px;
      height: 24px;
      border-radius: 50%;
      border: 3px solid white;
      box-shadow: 0 2px 6px rgba(0,0,0,0.4);
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;
      font-size: 12px;
      font-weight: bold;
    ">E</div>`,
    className: '',
    iconSize: [24, 24],
    iconAnchor: [12, 12]
  });

  const start = coordinates[0];
  const end = coordinates[coordinates.length - 1];

  return (
    <>
      <Marker position={[start.lat, start.lng]} icon={startIcon}>
        <Popup>
          <div className="text-sm">
            <p className="font-bold text-green-600">Start Point</p>
            <p className="text-gray-600">{routeName}</p>
          </div>
        </Popup>
      </Marker>
      <Marker position={[end.lat, end.lng]} icon={endIcon}>
        <Popup>
          <div className="text-sm">
            <p className="font-bold text-red-600">End Point</p>
            <p className="text-gray-600">{routeName}</p>
          </div>
        </Popup>
      </Marker>
    </>
  );
};

const VanRouteManagement = () => {
  const [routes, setRoutes] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [editingRoute, setEditingRoute] = useState(null);
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [activeTab, setActiveTab] = useState('details');
  const [editingStop, setEditingStop] = useState(null);
  const [isAddingStop, setIsAddingStop] = useState(false);

  // Route point editing state
  const [pointEditMode, setPointEditMode] = useState(false); // Enable dragging/editing points
  const [showPointsList, setShowPointsList] = useState(false); // Show points list panel
  const [navPointIndex, setNavPointIndex] = useState(0); // Current point index for navigation
  const [isNavigating, setIsNavigating] = useState(false); // Whether navigation mode is active
  const editMapRef = useRef(null); // Ref to the edit map for navigation

  // Auto-generate routes state
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generatedRoutes, setGeneratedRoutes] = useState([]);
  const [generateStats, setGenerateStats] = useState(null);
  const [selectedDays, setSelectedDays] = useState(['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']);
  const [previewRouteIndex, setPreviewRouteIndex] = useState(null);

  // Quadrant route generation state
  const [showQuadrantModal, setShowQuadrantModal] = useState(false);
  const [generatingQuadrants, setGeneratingQuadrants] = useState(false);
  const [quadrantRoutes, setQuadrantRoutes] = useState([]);
  const [quadrantStats, setQuadrantStats] = useState(null);
  const [useRoadSnapping, setUseRoadSnapping] = useState(true);
  const [subdivideZones, setSubdivideZones] = useState(false); // Split N/S into 4 quadrants each (8 total)
  const [previewQuadrantIndex, setPreviewQuadrantIndex] = useState(null);

  // Reference overlay state - shows coverage points as faint overlay for manual route drawing
  const [overlayRoute, setOverlayRoute] = useState(null); // { name, coordinates, color }

  // Help/instructions panel visibility
  const [showHelp, setShowHelp] = useState(false);

  // Fullscreen map (route create/edit)
  const [mapFullscreen, setMapFullscreen] = useState(false);

  // Exit fullscreen map with the Escape key
  useEffect(() => {
    if (!mapFullscreen) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setMapFullscreen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [mapFullscreen]);

  const [routeForm, setRouteForm] = useState({
    name: '',
    color: '#3B82F6', // Default blue
    coordinates: [],
    description: '',
    is_active: true,
    schedule_days: [],
    average_speed_kmh: 30.0,
    eta_enabled: false,
    assigned_vehicle_ids: [],
    stops: []
  });

  const [stopForm, setStopForm] = useState({
    name: '',
    description: '',
    position: null,
    schedule: [],
    sequence: 1,
    is_active: true
  });

  // Predefined color palette for routes
  const colorPalette = [
    { name: 'Blue', value: '#3B82F6' },
    { name: 'Green', value: '#10B981' },
    { name: 'Red', value: '#EF4444' },
    { name: 'Orange', value: '#F97316' },
    { name: 'Purple', value: '#8B5CF6' },
    { name: 'Pink', value: '#EC4899' },
    { name: 'Teal', value: '#14B8A6' },
    { name: 'Yellow', value: '#F59E0B' }
  ];

  // Load routes and vehicles on component mount
  useEffect(() => {
    loadRoutes();
    loadVehicles();
  }, []);

  const loadRoutes = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/van-routes/`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
        }
      });
      setRoutes(response.data);
      setError('');
    } catch (error) {
      setError('Failed to load truck routes');
    } finally {
      setLoading(false);
    }
  };

  const loadVehicles = async () => {
    try {
      // Use /vehicles/public to get all public vehicles (garbage trucks)
      // The /vehicles/ endpoint only returns vehicles owned by the current user
      const response = await axios.get(`${API}/vehicles/public`);
      setVehicles(response.data);
    } catch (error) {
      console.error('Failed to load vehicles:', error);
    }
  };

  const handleCreateRoute = () => {
    setIsCreating(true);
    setEditingRoute(null);
    setSelectedRoute(null);
    setActiveTab('details');
    setIsNavigating(false);
    setNavPointIndex(0);
    setRouteForm({
      name: '',
      color: '#3B82F6',
      coordinates: [],
      description: '',
      is_active: true,
      schedule_days: [],
      average_speed_kmh: 30.0,
      eta_enabled: false,
      assigned_vehicle_ids: [],
      stops: []
    });
  };

  const handleEditRoute = async (routeId) => {
    try {
      const response = await axios.get(`${API}/admin/van-routes/${routeId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
        }
      });

      const route = response.data;
      setEditingRoute(route);
      setIsCreating(true);
      setSelectedRoute(null);
      setActiveTab('details');
      setRouteForm({
        name: route.name,
        color: route.color,
        coordinates: route.coordinates,
        description: route.description || '',
        is_active: route.is_active,
        schedule_days: route.schedule_days || [],
        average_speed_kmh: route.average_speed_kmh || 30.0,
        eta_enabled: route.eta_enabled || false,
        assigned_vehicle_ids: route.assigned_vehicle_ids || [],
        stops: route.stops || []
      });
    } catch (error) {
      setError('Failed to load route for editing');
    }
  };

  const handleSaveRoute = async () => {
    if (!routeForm.name.trim()) {
      setError('Route name is required');
      return;
    }

    if (routeForm.coordinates.length < 2) {
      setError('Route must have at least 2 points');
      return;
    }

    try {
      if (editingRoute) {
        // Update existing route
        await axios.put(
          `${API}/admin/van-routes/${editingRoute.id}`,
          routeForm,
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
            }
          }
        );
        setSuccess('Route updated successfully');
      } else {
        // Create new route
        await axios.post(
          `${API}/admin/van-routes/`,
          routeForm,
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
            }
          }
        );
        setSuccess('Route created successfully');
      }

      setIsCreating(false);
      setEditingRoute(null);
      setPointEditMode(false);
      setShowPointsList(false);
      setIsNavigating(false);
      setNavPointIndex(0);
      loadRoutes();

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to save route');
    }
  };

  const handleDeleteRoute = async (routeId) => {
    if (!window.confirm('Are you sure you want to delete this route?')) {
      return;
    }

    try {
      await axios.delete(`${API}/admin/van-routes/${routeId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
        }
      });
      setSuccess('Route deleted successfully');
      loadRoutes();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError('Failed to delete route');
    }
  };

  const handleToggleActive = async (route) => {
    try {
      await axios.put(
        `${API}/admin/van-routes/${route.id}`,
        { is_active: !route.is_active },
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
          }
        }
      );
      setSuccess(`Route ${!route.is_active ? 'activated' : 'deactivated'} successfully`);
      loadRoutes();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError('Failed to update route status');
    }
  };

  const handleViewRoute = async (routeId) => {
    try {
      const response = await axios.get(`${API}/admin/van-routes/${routeId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
        }
      });
      setSelectedRoute(response.data);
      setIsCreating(false);
      setEditingRoute(null);
      setIsNavigating(false);
      setNavPointIndex(0);
    } catch (error) {
      setError('Failed to load route');
    }
  };

  const handleClearDrawing = () => {
    setRouteForm(prev => ({ ...prev, coordinates: [] }));
  };

  const handleUndoLastPoint = () => {
    setRouteForm(prev => ({
      ...prev,
      coordinates: prev.coordinates.slice(0, -1)
    }));
  };

  // Point editing functions
  const handlePointDragEnd = (index, newPosition) => {
    setRouteForm(prev => ({
      ...prev,
      coordinates: prev.coordinates.map((coord, i) =>
        i === index ? newPosition : coord
      )
    }));
  };

  const handleDeletePoint = (index) => {
    if (routeForm.coordinates.length <= 2) {
      setError('Route must have at least 2 points');
      setTimeout(() => setError(''), 3000);
      return;
    }
    setRouteForm(prev => ({
      ...prev,
      coordinates: prev.coordinates.filter((_, i) => i !== index)
    }));
  };

  const handleInsertPoint = (index, position) => {
    setRouteForm(prev => ({
      ...prev,
      coordinates: [
        ...prev.coordinates.slice(0, index),
        position,
        ...prev.coordinates.slice(index)
      ]
    }));
  };

  const handleMovePointUp = (index) => {
    if (index === 0) return;
    setRouteForm(prev => {
      const newCoords = [...prev.coordinates];
      [newCoords[index - 1], newCoords[index]] = [newCoords[index], newCoords[index - 1]];
      return { ...prev, coordinates: newCoords };
    });
  };

  const handleMovePointDown = (index) => {
    if (index >= routeForm.coordinates.length - 1) return;
    setRouteForm(prev => {
      const newCoords = [...prev.coordinates];
      [newCoords[index], newCoords[index + 1]] = [newCoords[index + 1], newCoords[index]];
      return { ...prev, coordinates: newCoords };
    });
  };

  const handleReverseRoute = () => {
    setRouteForm(prev => ({
      ...prev,
      coordinates: [...prev.coordinates].reverse()
    }));
  };

  // Stop Management Functions
  const handleAddStop = () => {
    setIsAddingStop(true);
    setEditingStop(null);
    setStopForm({
      name: '',
      description: '',
      position: null,
      schedule: [],
      sequence: routeForm.stops.length + 1,
      is_active: true
    });
  };

  const handleEditStop = (stop) => {
    setIsAddingStop(true);
    setEditingStop(stop);
    setStopForm({
      name: stop.name,
      description: stop.description || '',
      position: stop.position,
      schedule: stop.schedule || [],
      sequence: stop.sequence,
      is_active: stop.is_active
    });
  };

  const handleSaveStop = async () => {
    if (!stopForm.name.trim()) {
      setError('Stop name is required');
      return;
    }

    if (!stopForm.position) {
      setError('Stop position is required - click on the map to place the stop');
      return;
    }

    try {
      if (editingRoute) {
        // If we're editing an existing route, use the API
        if (editingStop) {
          // Update existing stop
          await axios.put(
            `${API}/admin/van-routes/${editingRoute.id}/stops/${editingStop.id}`,
            stopForm,
            {
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
              }
            }
          );
          setSuccess('Stop updated successfully');
        } else {
          // Add new stop
          await axios.post(
            `${API}/admin/van-routes/${editingRoute.id}/stops`,
            stopForm,
            {
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
              }
            }
          );
          setSuccess('Stop added successfully');
        }

        // Reload the route to get updated stops
        const response = await axios.get(`${API}/admin/van-routes/${editingRoute.id}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
          }
        });
        setRouteForm(prev => ({ ...prev, stops: response.data.stops }));
      } else {
        // If we're creating a new route, just update local state
        if (editingStop) {
          setRouteForm(prev => ({
            ...prev,
            stops: prev.stops.map(s => s.id === editingStop.id ? { ...stopForm, id: editingStop.id } : s)
          }));
        } else {
          const newStop = {
            ...stopForm,
            id: `temp-${Date.now()}` // Temporary ID for new routes
          };
          setRouteForm(prev => ({
            ...prev,
            stops: [...prev.stops, newStop]
          }));
        }
        setSuccess('Stop added to route');
      }

      setIsAddingStop(false);
      setEditingStop(null);
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to save stop');
    }
  };

  const handleDeleteStop = async (stop) => {
    if (!window.confirm(`Are you sure you want to delete stop "${stop.name}"?`)) {
      return;
    }

    try {
      if (editingRoute && stop.id && !stop.id.startsWith('temp-')) {
        // Delete from server
        await axios.delete(`${API}/admin/van-routes/${editingRoute.id}/stops/${stop.id}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
          }
        });
        setSuccess('Stop deleted successfully');

        // Reload the route
        const response = await axios.get(`${API}/admin/van-routes/${editingRoute.id}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
          }
        });
        setRouteForm(prev => ({ ...prev, stops: response.data.stops }));
      } else {
        // Remove from local state
        setRouteForm(prev => ({
          ...prev,
          stops: prev.stops.filter(s => s.id !== stop.id)
        }));
        setSuccess('Stop removed from route');
      }

      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError('Failed to delete stop');
    }
  };

  const handleAddScheduleTime = () => {
    setStopForm(prev => ({
      ...prev,
      schedule: [...prev.schedule, { time: '08:00', days: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'] }]
    }));
  };

  const handleRemoveScheduleTime = (index) => {
    setStopForm(prev => ({
      ...prev,
      schedule: prev.schedule.filter((_, i) => i !== index)
    }));
  };

  const handleUpdateScheduleTime = (index, field, value) => {
    setStopForm(prev => ({
      ...prev,
      schedule: prev.schedule.map((sched, i) =>
        i === index ? { ...sched, [field]: value } : sched
      )
    }));
  };

  const toggleScheduleDay = (index, day) => {
    setStopForm(prev => ({
      ...prev,
      schedule: prev.schedule.map((sched, i) => {
        if (i !== index) return sched;
        const days = sched.days.includes(day)
          ? sched.days.filter(d => d !== day)
          : [...sched.days, day];
        return { ...sched, days };
      })
    }));
  };

  // Auto-generate routes functions
  const handleOpenGenerateModal = () => {
    setShowGenerateModal(true);
    setGeneratedRoutes([]);
    setGenerateStats(null);
    setPreviewRouteIndex(null);
  };

  const handleCloseGenerateModal = () => {
    setShowGenerateModal(false);
    setGeneratedRoutes([]);
    setGenerateStats(null);
    setPreviewRouteIndex(null);
  };

  const handleToggleGenerateDay = (day) => {
    setSelectedDays(prev =>
      prev.includes(day)
        ? prev.filter(d => d !== day)
        : [...prev, day]
    );
  };

  const handleGenerateRoutes = async () => {
    if (selectedDays.length === 0) {
      setError('Please select at least one day');
      return;
    }

    try {
      setGenerating(true);
      setError('');

      const response = await axios.post(
        `${API}/admin/van-routes/generate`,
        { days: selectedDays },
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
          }
        }
      );

      setGeneratedRoutes(response.data.routes || []);
      setGenerateStats(response.data.statistics || null);
      setSuccess(`Generated ${response.data.routes?.length || 0} routes from GPS history`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to generate routes');
    } finally {
      setGenerating(false);
    }
  };

  const handleSaveGeneratedRoutes = async (replaceExisting = false) => {
    if (generatedRoutes.length === 0) {
      setError('No routes to save');
      return;
    }

    try {
      setGenerating(true);
      setError('');

      const response = await axios.post(
        `${API}/admin/van-routes/generate/save`,
        {
          routes: generatedRoutes,
          replace_existing: replaceExisting
        },
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
          }
        }
      );

      setSuccess(`Saved ${response.data.saved_count} routes successfully`);
      handleCloseGenerateModal();
      loadRoutes();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to save routes');
    } finally {
      setGenerating(false);
    }
  };

  const handleDeleteAutoGeneratedRoutes = async () => {
    if (!window.confirm('Are you sure you want to delete ALL auto-generated routes?')) {
      return;
    }

    try {
      const response = await axios.delete(`${API}/admin/van-routes/generated`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
        }
      });
      setSuccess(`Deleted ${response.data.deleted_count} auto-generated routes`);
      loadRoutes();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError('Failed to delete auto-generated routes');
    }
  };

  // Quadrant route generation functions
  const handleOpenQuadrantModal = () => {
    setShowQuadrantModal(true);
    setQuadrantRoutes([]);
    setQuadrantStats(null);
    setPreviewQuadrantIndex(null);
  };

  const handleCloseQuadrantModal = () => {
    setShowQuadrantModal(false);
    setQuadrantRoutes([]);
    setQuadrantStats(null);
    setPreviewQuadrantIndex(null);
  };

  const handleGenerateQuadrantRoutes = async () => {
    try {
      setGeneratingQuadrants(true);
      setError('');

      const response = await axios.post(
        `${API}/admin/van-routes/generate-quadrants`,
        {
          use_road_snapping: useRoadSnapping,
          subdivide: subdivideZones
        },
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
          },
          timeout: 600000 // 10 minute timeout for road snapping (8-quadrant takes longer)
        }
      );

      setQuadrantRoutes(response.data.routes || []);
      setQuadrantStats(response.data.statistics || null);
      const mode = subdivideZones ? '8-quadrant' : '2-zone';
      setSuccess(`Generated ${response.data.routes?.length || 0} ${mode} routes`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to generate quadrant routes');
    } finally {
      setGeneratingQuadrants(false);
    }
  };

  const handleSaveQuadrantRoutes = async () => {
    if (quadrantRoutes.length === 0) {
      setError('No routes to save');
      return;
    }

    try {
      setGeneratingQuadrants(true);
      setError('');

      const response = await axios.post(
        `${API}/admin/van-routes/generate-quadrants/save`,
        {
          routes: quadrantRoutes,
          replace_existing: true
        },
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
          }
        }
      );

      setSuccess(`Saved ${response.data.saved_count} quadrant routes`);
      handleCloseQuadrantModal();
      loadRoutes();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to save quadrant routes');
    } finally {
      setGeneratingQuadrants(false);
    }
  };

  // Map component for drawing routes and placing stops
  const MapClickHandler = () => {
    useMapEvents({
      click: (e) => {
        if (isCreating && activeTab === 'details' && !pointEditMode) {
          // Add route coordinate (only when not in point edit mode)
          const { lat, lng } = e.latlng;
          setRouteForm(prev => ({
            ...prev,
            coordinates: [...prev.coordinates, { lat, lng }]
          }));
        } else if (isCreating && activeTab === 'stops' && isAddingStop) {
          // Place stop on map
          const { lat, lng } = e.latlng;
          setStopForm(prev => ({
            ...prev,
            position: { lat, lng }
          }));
        }
      }
    });
    return null;
  };

  // Create markers for route points
  const createPointIcon = (index, total) => {
    const isFirst = index === 0;
    const isLast = index === total - 1;
    const color = isFirst ? '#10B981' : isLast ? '#EF4444' : '#3B82F6';

    return L.divIcon({
      html: `<div style="
        background-color: ${color};
        width: 12px;
        height: 12px;
        border-radius: 50%;
        border: 2px solid white;
        box-shadow: 0 2px 4px rgba(0,0,0,0.3);
      "></div>`,
      className: '',
      iconSize: [12, 12],
      iconAnchor: [6, 6]
    });
  };

  // Create markers for stops
  const createStopIcon = (sequence, color) => {
    return L.divIcon({
      html: `<div style="
        background: white;
        border: 3px solid ${color};
        border-radius: 50%;
        width: 30px;
        height: 30px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: bold;
        font-size: 14px;
        color: ${color};
        box-shadow: 0 2px 4px rgba(0,0,0,0.3);
      ">${sequence}</div>`,
      className: '',
      iconSize: [30, 30],
      iconAnchor: [15, 15]
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">🚛 Garbage Truck Routes</h2>
          <p className="text-gray-600 mt-1">
            Create and manage garbage truck collection routes for Belmopan
          </p>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={() => setShowHelp(!showHelp)}
            className={`px-4 py-2 rounded-md flex items-center ${
              showHelp ? 'bg-indigo-600 text-white' : 'bg-indigo-100 text-indigo-700 hover:bg-indigo-200'
            }`}
          >
            <span className="mr-2">❓</span>
            How It Works
          </button>
          <button
            onClick={handleOpenQuadrantModal}
            className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 flex items-center"
            disabled={isCreating}
          >
            <span className="mr-2">🧭</span>
            Zone Routes
          </button>
          <button
            onClick={handleOpenGenerateModal}
            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center"
            disabled={isCreating}
          >
            <span className="mr-2">🔄</span>
            Auto-Generate from GPS
          </button>
          <button
            onClick={handleCreateRoute}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center"
            disabled={isCreating}
          >
            <span className="mr-2">+</span>
            Create New Route
          </button>
        </div>
      </div>

      {/* Error/Success Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
          <button
            onClick={() => setError('')}
            className="float-right text-red-700 hover:text-red-900"
          >
            ✕
          </button>
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded">
          {success}
        </div>
      )}

      {/* How It Works - Instructions Panel */}
      {showHelp && (
        <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-5 space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold text-indigo-900">❓ How Garbage Truck Routes Work</h3>
            <button onClick={() => setShowHelp(false)} className="text-indigo-500 hover:text-indigo-700">✕ Close</button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-indigo-900">
            <div className="bg-white rounded-lg p-4">
              <h4 className="font-semibold mb-2">📝 Creating a route (step by step)</h4>
              <ol className="list-decimal ml-4 space-y-1">
                <li>Click <strong>+ Create New Route</strong>.</li>
                <li>Give it a clear name (e.g., "North Zone – Mon/Thu").</li>
                <li>Pick the <strong>Collection Days</strong> — the days of the week the truck runs this route. Use the quick presets or tap individual days.</li>
                <li>Pick a color so the route is easy to recognize on the map.</li>
                <li><strong>Click on the map</strong> to draw the path, point by point, in the exact order the truck should drive it. The green marker is the start, the red marker is the end.</li>
                <li>Made a mistake? Use <strong>← Undo Last</strong>, <strong>✏️ Edit Points</strong> (drag markers, click the line to insert points), or <strong>📋 List</strong> to reorder/delete points.</li>
                <li>Optional: open the <strong>Stops</strong> tab to add named stops with pickup times.</li>
                <li>Under <strong>Assigned Vehicles</strong>, select which truck(s) drive this route — this is what makes the route appear in that truck driver's app.</li>
                <li>Click <strong>💾 Save Route</strong>.</li>
              </ol>
            </div>

            <div className="space-y-4">
              <div className="bg-white rounded-lg p-4">
                <h4 className="font-semibold mb-2">📅 What Collection Days control</h4>
                <p>
                  A driver logging in at <span className="font-mono bg-gray-100 px-1 rounded">/driver</span> sees routes
                  assigned to their truck. Routes scheduled for the current day appear under
                  <strong> "Today's Routes"</strong> and are highlighted; other routes appear under "Other Days".
                  A route with <strong>no days selected</strong> is treated as available every day.
                </p>
              </div>

              <div className="bg-white rounded-lg p-4">
                <h4 className="font-semibold mb-2">🔗 The full workflow (no ambiguity)</h4>
                <ol className="list-decimal ml-4 space-y-1">
                  <li><strong>Garbage Trucks</strong> tab: register the truck (with its GPS device).</li>
                  <li><strong>This tab</strong>: create the route, set collection days, assign the truck.</li>
                  <li><strong>Drivers</strong> tab: create the driver and assign them to the truck.</li>
                  <li>Driver logs in at <span className="font-mono bg-gray-100 px-1 rounded">/driver</span> and presses <strong>▶ Start Route</strong> — they see live if they are ON or OFF the route.</li>
                </ol>
              </div>

              <div className="bg-white rounded-lg p-4">
                <h4 className="font-semibold mb-2">🛠️ Other buttons on this page</h4>
                <ul className="list-disc ml-4 space-y-1">
                  <li><strong>👁️ Activate/Deactivate</strong> — inactive routes are hidden from the public map and from drivers, without deleting them.</li>
                  <li><strong>📋 Use as Reference Overlay</strong> — shows an existing route as orange dots/dashes so you can trace a new route over it.</li>
                  <li><strong>🧭 Zone Routes / 🔄 Auto-Generate from GPS</strong> — build draft routes automatically from past truck GPS history; review, then save and refine.</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Side - Route List or Form */}
        <div className="space-y-4">
          {isCreating ? (
            /* Route Creation/Edit Form */
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="p-6 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">
                  {editingRoute ? 'Edit Route' : 'Create New Route'}
                </h3>
              </div>

              {/* Tabs - Show for both creating and editing routes */}
              <div className="border-b border-gray-200">
                <div className="flex">
                  <button
                    onClick={() => setActiveTab('details')}
                    className={`px-6 py-3 font-medium text-sm transition-colors ${
                      activeTab === 'details'
                        ? 'border-b-2 border-blue-600 text-blue-600 bg-blue-50'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                    }`}
                  >
                    Route Details
                  </button>
                  <button
                    onClick={() => setActiveTab('stops')}
                    className={`px-6 py-3 font-medium text-sm transition-colors ${
                      activeTab === 'stops'
                        ? 'border-b-2 border-blue-600 text-blue-600 bg-blue-50'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                    }`}
                  >
                    Stops ({routeForm.stops.length})
                  </button>
                </div>
              </div>

              <div className="p-6">
                {activeTab === 'details' && (
                  /* Route Details Tab */
                  <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Route Name *
                  </label>
                  <input
                    type="text"
                    value={routeForm.name}
                    onChange={(e) => setRouteForm(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., Western Route"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Collection Days
                  </label>
                  <div className="grid grid-cols-7 gap-1 mb-2">
                    {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day) => (
                      <button
                        key={day}
                        type="button"
                        onClick={() => setRouteForm(prev => ({
                          ...prev,
                          schedule_days: prev.schedule_days.includes(day)
                            ? prev.schedule_days.filter(d => d !== day)
                            : [...prev.schedule_days, day]
                        }))}
                        className={`text-sm px-1 py-2 rounded font-medium transition-colors ${
                          routeForm.schedule_days.includes(day)
                            ? 'bg-green-600 text-white'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        {day}
                      </button>
                    ))}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => setRouteForm(prev => ({ ...prev, schedule_days: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'] }))}
                      className="text-xs px-2 py-1 bg-blue-50 text-blue-700 rounded hover:bg-blue-100"
                    >
                      Weekdays
                    </button>
                    <button
                      type="button"
                      onClick={() => setRouteForm(prev => ({ ...prev, schedule_days: ['Mon', 'Wed', 'Fri'] }))}
                      className="text-xs px-2 py-1 bg-blue-50 text-blue-700 rounded hover:bg-blue-100"
                    >
                      Mon / Wed / Fri
                    </button>
                    <button
                      type="button"
                      onClick={() => setRouteForm(prev => ({ ...prev, schedule_days: ['Tue', 'Thu', 'Sat'] }))}
                      className="text-xs px-2 py-1 bg-blue-50 text-blue-700 rounded hover:bg-blue-100"
                    >
                      Tue / Thu / Sat
                    </button>
                    <button
                      type="button"
                      onClick={() => setRouteForm(prev => ({ ...prev, schedule_days: [] }))}
                      className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded hover:bg-gray-200"
                    >
                      Clear
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Days the garbage truck runs this route. Drivers will see it highlighted on those days.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Route Color
                  </label>
                  <div className="grid grid-cols-4 gap-2">
                    {colorPalette.map((color) => (
                      <button
                        key={color.value}
                        onClick={() => setRouteForm(prev => ({ ...prev, color: color.value }))}
                        className={`p-3 rounded-md border-2 transition-all ${
                          routeForm.color === color.value
                            ? 'border-gray-900 ring-2 ring-blue-500'
                            : 'border-gray-300 hover:border-gray-400'
                        }`}
                        style={{ backgroundColor: color.value }}
                        title={color.name}
                      >
                        {routeForm.color === color.value && (
                          <span className="text-white font-bold">✓</span>
                        )}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description (Optional)
                  </label>
                  <textarea
                    value={routeForm.description}
                    onChange={(e) => setRouteForm(prev => ({ ...prev, description: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows="3"
                    placeholder="Optional route description"
                  />
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="is_active"
                    checked={routeForm.is_active}
                    onChange={(e) => setRouteForm(prev => ({ ...prev, is_active: e.target.checked }))}
                    className="mr-2 h-4 w-4 text-blue-600"
                  />
                  <label htmlFor="is_active" className="text-sm text-gray-700">
                    Active (visible on map)
                  </label>
                </div>

                <div className="border-t border-gray-200 pt-4 space-y-4">
                  <h4 className="text-sm font-semibold text-gray-900">Real-time ETA Settings</h4>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="eta_enabled"
                      checked={routeForm.eta_enabled}
                      onChange={(e) => setRouteForm(prev => ({ ...prev, eta_enabled: e.target.checked }))}
                      className="mr-2 h-4 w-4 text-blue-600"
                    />
                    <label htmlFor="eta_enabled" className="text-sm text-gray-700">
                      Enable Real-time ETA Display (shows arrival times on frontend)
                    </label>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Average Speed (km/h)
                    </label>
                    <input
                      type="number"
                      value={routeForm.average_speed_kmh}
                      onChange={(e) => setRouteForm(prev => ({ ...prev, average_speed_kmh: parseFloat(e.target.value) || 30.0 }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      min="1"
                      max="100"
                      step="0.1"
                    />
                    <p className="text-xs text-gray-500 mt-1">Used for calculating estimated arrival times</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Assigned Vehicles
                    </label>
                    <select
                      multiple
                      value={routeForm.assigned_vehicle_ids}
                      onChange={(e) => {
                        const selected = Array.from(e.target.selectedOptions, option => option.value);
                        setRouteForm(prev => ({ ...prev, assigned_vehicle_ids: selected }));
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      size="5"
                    >
                      {vehicles.map((vehicle) => (
                        <option key={vehicle.id} value={vehicle.id}>
                          {vehicle.license_plate} - {`${vehicle.make || ''} ${vehicle.model || ''}`.trim() || 'Truck'}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      Hold Ctrl/Cmd to select multiple vehicles. Selected: {routeForm.assigned_vehicle_ids.length}
                    </p>
                  </div>
                </div>

                <div className="border-t border-gray-200 pt-4">
                  <div className="flex justify-between items-center mb-2">
                    <p className="text-sm font-medium text-gray-700">
                      Route Points: {routeForm.coordinates.length}
                    </p>
                    {routeForm.coordinates.length > 0 && (
                      <div className="flex space-x-1">
                        <button
                          onClick={() => setPointEditMode(!pointEditMode)}
                          className={`text-xs px-2 py-1 rounded transition-colors ${
                            pointEditMode
                              ? 'bg-blue-600 text-white'
                              : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                          }`}
                          title={pointEditMode ? 'Exit edit mode' : 'Edit points (drag to move)'}
                        >
                          {pointEditMode ? '✓ Editing' : '✏️ Edit Points'}
                        </button>
                        <button
                          onClick={() => setShowPointsList(!showPointsList)}
                          className={`text-xs px-2 py-1 rounded transition-colors ${
                            showPointsList
                              ? 'bg-purple-600 text-white'
                              : 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                          }`}
                          title="Show/hide points list"
                        >
                          {showPointsList ? '✓ List' : '📋 List'}
                        </button>
                        <button
                          onClick={() => {
                            setIsNavigating(!isNavigating);
                            if (!isNavigating) setNavPointIndex(0);
                          }}
                          className={`text-xs px-2 py-1 rounded transition-colors ${
                            isNavigating
                              ? 'bg-green-600 text-white'
                              : 'bg-green-100 text-green-700 hover:bg-green-200'
                          }`}
                          title="Follow route point by point"
                        >
                          {isNavigating ? '✓ Following' : '🚗 Follow'}
                        </button>
                      </div>
                    )}
                  </div>

                  {pointEditMode ? (
                    <div className="bg-blue-50 border border-blue-200 rounded p-2 mb-2">
                      <p className="text-xs text-blue-800">
                        <strong>Edit Mode Active:</strong> Drag markers to move points. Click markers to delete. Click on the route line to insert new points.
                      </p>
                    </div>
                  ) : (
                    <p className="text-xs text-gray-500 mb-2">
                      Click on the map to add points. Minimum 2 points required.
                    </p>
                  )}

                  {/* Points List Panel */}
                  {showPointsList && routeForm.coordinates.length > 0 && (
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-3 max-h-48 overflow-y-auto">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-xs font-medium text-gray-700">Points List</span>
                        <button
                          onClick={handleReverseRoute}
                          className="text-xs px-2 py-1 bg-orange-100 text-orange-700 rounded hover:bg-orange-200"
                          title="Reverse route direction"
                        >
                          ↔️ Reverse
                        </button>
                      </div>
                      <div className="space-y-1">
                        {routeForm.coordinates.map((coord, index) => (
                          <div
                            key={index}
                            className="flex items-center justify-between bg-white rounded px-2 py-1 text-xs border border-gray-100"
                          >
                            <div className="flex items-center space-x-2">
                              <span
                                className="w-5 h-5 rounded-full flex items-center justify-center text-white text-[10px] font-bold"
                                style={{
                                  backgroundColor: index === 0 ? '#10B981' : index === routeForm.coordinates.length - 1 ? '#EF4444' : routeForm.color
                                }}
                              >
                                {index + 1}
                              </span>
                              <span className="text-gray-600 font-mono">
                                {coord.lat.toFixed(4)}, {coord.lng.toFixed(4)}
                              </span>
                            </div>
                            <div className="flex items-center space-x-1">
                              <button
                                onClick={() => handleMovePointUp(index)}
                                disabled={index === 0}
                                className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
                                title="Move up"
                              >
                                ↑
                              </button>
                              <button
                                onClick={() => handleMovePointDown(index)}
                                disabled={index === routeForm.coordinates.length - 1}
                                className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
                                title="Move down"
                              >
                                ↓
                              </button>
                              <button
                                onClick={() => handleDeletePoint(index)}
                                disabled={routeForm.coordinates.length <= 2}
                                className="p-1 text-red-400 hover:text-red-600 disabled:opacity-30 disabled:cursor-not-allowed"
                                title="Delete point"
                              >
                                ✕
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {routeForm.coordinates.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={handleUndoLastPoint}
                        className="text-xs px-3 py-1 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                      >
                        ← Undo Last
                      </button>
                      <button
                        onClick={handleClearDrawing}
                        className="text-xs px-3 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200"
                      >
                        Clear All
                      </button>
                    </div>
                  )}
                </div>

                <div className="flex space-x-2 pt-4 border-t border-gray-200 mt-4">
                  <button
                    onClick={handleSaveRoute}
                    disabled={!routeForm.name.trim() || routeForm.coordinates.length < 2}
                    className="flex-1 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    💾 Save Route
                  </button>
                  <button
                    onClick={() => {
                      setIsCreating(false);
                      setEditingRoute(null);
                      setPointEditMode(false);
                      setShowPointsList(false);
                      setIsNavigating(false);
                      setNavPointIndex(0);
                    }}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {activeTab === 'stops' && (
              <div className="space-y-4">
                {!isAddingStop ? (
                  /* Stops List View */
                  <>
                    <div className="flex justify-between items-center">
                      <p className="text-sm text-gray-600">
                        Manage scheduled stops along this route
                      </p>
                      <button
                        onClick={handleAddStop}
                        className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm flex items-center"
                      >
                        <span className="mr-1">+</span>
                        Add Stop
                      </button>
                    </div>

                    {routeForm.stops.length === 0 ? (
                      <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-lg">
                        <div className="text-4xl mb-2">🚏</div>
                        <p className="text-gray-600 font-medium mb-1">No stops yet</p>
                        <p className="text-xs text-gray-500">Click "Add Stop" to create your first stop</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {routeForm.stops
                          .sort((a, b) => a.sequence - b.sequence)
                          .map((stop) => (
                            <div
                              key={stop.id}
                              className="border border-gray-200 rounded-lg p-3 hover:border-blue-300 transition-colors"
                            >
                              <div className="flex items-start justify-between">
                                <div className="flex items-start space-x-3 flex-1">
                                  <div
                                    className="w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm border-2"
                                    style={{
                                      borderColor: routeForm.color,
                                      color: routeForm.color
                                    }}
                                  >
                                    {stop.sequence}
                                  </div>
                                  <div className="flex-1">
                                    <h4 className="font-medium text-gray-900">{stop.name}</h4>
                                    {stop.description && (
                                      <p className="text-xs text-gray-500 mt-1">{stop.description}</p>
                                    )}
                                    <p className="text-xs text-gray-400 mt-1">
                                      📍 {stop.position.lat.toFixed(5)}, {stop.position.lng.toFixed(5)}
                                    </p>
                                    {stop.schedule && stop.schedule.length > 0 && (
                                      <div className="mt-2">
                                        <p className="text-xs font-medium text-gray-600 mb-1">Schedule:</p>
                                        <div className="flex flex-wrap gap-1">
                                          {stop.schedule.map((sched, idx) => (
                                            <span
                                              key={idx}
                                              className="text-xs px-2 py-1 bg-blue-50 text-blue-700 rounded"
                                            >
                                              {sched.time} ({sched.days.length} days)
                                            </span>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </div>
                                <div className="flex space-x-1 ml-2">
                                  <button
                                    onClick={() => handleEditStop(stop)}
                                    className="p-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                                    title="Edit"
                                  >
                                    ✏️
                                  </button>
                                  <button
                                    onClick={() => handleDeleteStop(stop)}
                                    className="p-1 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200"
                                    title="Delete"
                                  >
                                    🗑️
                                  </button>
                                </div>
                              </div>
                            </div>
                          ))}
                      </div>
                    )}

                    <div className="flex space-x-2 pt-4 border-t border-gray-200">
                      <button
                        onClick={() => {
                          setIsCreating(false);
                          setEditingRoute(null);
                          setIsNavigating(false);
                          setNavPointIndex(0);
                        }}
                        className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
                      >
                        Close
                      </button>
                    </div>
                  </>
                ) : (
                  /* Add/Edit Stop Form */
                  <div className="space-y-4">
                    <div className="bg-blue-50 border border-blue-200 rounded p-3 mb-4">
                      <p className="text-sm text-blue-800">
                        📍 Click on the map to place this stop on the route
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Stop Name *
                      </label>
                      <input
                        type="text"
                        value={stopForm.name}
                        onChange={(e) => setStopForm(prev => ({ ...prev, name: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="e.g., Market, Main Street, City Center"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Description (Optional)
                      </label>
                      <textarea
                        value={stopForm.description}
                        onChange={(e) => setStopForm(prev => ({ ...prev, description: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        rows="2"
                        placeholder="Additional details about this stop"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Position *
                      </label>
                      {stopForm.position ? (
                        <div className="px-3 py-2 bg-green-50 border border-green-200 rounded-md text-sm text-green-700">
                          ✓ {stopForm.position.lat.toFixed(5)}, {stopForm.position.lng.toFixed(5)}
                        </div>
                      ) : (
                        <div className="px-3 py-2 bg-yellow-50 border border-yellow-200 rounded-md text-sm text-yellow-700">
                          Click on the map to set stop position
                        </div>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Sequence
                      </label>
                      <input
                        type="number"
                        value={stopForm.sequence}
                        onChange={(e) => setStopForm(prev => ({ ...prev, sequence: parseInt(e.target.value) || 1 }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        min="1"
                      />
                      <p className="text-xs text-gray-500 mt-1">Order of this stop along the route</p>
                    </div>

                    <div className="border-t border-gray-200 pt-4">
                      <div className="flex justify-between items-center mb-3">
                        <label className="block text-sm font-medium text-gray-700">
                          Schedule Times
                        </label>
                        <button
                          onClick={handleAddScheduleTime}
                          className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded hover:bg-green-200"
                        >
                          + Add Time
                        </button>
                      </div>

                      {stopForm.schedule.length === 0 ? (
                        <p className="text-xs text-gray-500 italic">No schedule times yet</p>
                      ) : (
                        <div className="space-y-3">
                          {stopForm.schedule.map((sched, index) => (
                            <div key={index} className="border border-gray-200 rounded p-3 bg-gray-50">
                              <div className="flex justify-between items-start mb-2">
                                <label className="text-xs font-medium text-gray-700">Time</label>
                                <button
                                  onClick={() => handleRemoveScheduleTime(index)}
                                  className="text-xs text-red-600 hover:text-red-800"
                                >
                                  Remove
                                </button>
                              </div>
                              <input
                                type="time"
                                value={sched.time}
                                onChange={(e) => handleUpdateScheduleTime(index, 'time', e.target.value)}
                                className="w-full px-2 py-1 border border-gray-300 rounded text-sm mb-2"
                              />
                              <label className="text-xs font-medium text-gray-700 block mb-1">Days</label>
                              <div className="grid grid-cols-7 gap-1">
                                {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day) => (
                                  <button
                                    key={day}
                                    onClick={() => toggleScheduleDay(index, day)}
                                    className={`text-xs px-1 py-1 rounded transition-colors ${
                                      sched.days.includes(day)
                                        ? 'bg-blue-600 text-white'
                                        : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                                    }`}
                                  >
                                    {day.substring(0, 2)}
                                  </button>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="flex space-x-2 pt-4 border-t border-gray-200">
                      <button
                        onClick={handleSaveStop}
                        disabled={!stopForm.name.trim() || !stopForm.position}
                        className="flex-1 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {editingStop ? '💾 Update Stop' : '💾 Add Stop'}
                      </button>
                      <button
                        onClick={() => {
                          setIsAddingStop(false);
                          setEditingStop(null);
                        }}
                        className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      ) : (
            /* Route List */
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="p-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">
                  Existing Routes ({routes.length})
                </h3>
              </div>

              {loading ? (
                <div className="p-8 text-center">
                  <div className="text-gray-400 text-2xl mb-2">⏳</div>
                  <p className="text-gray-600">Loading routes...</p>
                </div>
              ) : routes.length === 0 ? (
                <div className="p-8 text-center">
                  <div className="text-gray-400 text-4xl mb-4">🗺️</div>
                  <p className="text-gray-600">No routes created yet</p>
                  <button
                    onClick={handleCreateRoute}
                    className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    Create First Route
                  </button>
                </div>
              ) : (
                <div className="divide-y divide-gray-200">
                  {routes.map((route) => (
                    <div
                      key={route.id}
                      className="p-4 hover:bg-gray-50 cursor-pointer"
                      onClick={() => handleViewRoute(route.id)}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-center space-x-3 flex-1">
                          <div
                            className="w-4 h-4 rounded-full border-2 border-white shadow"
                            style={{ backgroundColor: route.color }}
                          ></div>
                          <div className="flex-1">
                            <h4 className="font-medium text-gray-900">{route.name}</h4>
                            <p className="text-xs text-gray-500">
                              {route.coordinate_count} points •{' '}
                              {route.is_active ? (
                                <span className="text-green-600">Active</span>
                              ) : (
                                <span className="text-gray-400">Inactive</span>
                              )}
                            </p>
                            {route.schedule_days && route.schedule_days.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-1">
                                {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
                                  .filter(d => route.schedule_days.includes(d))
                                  .map(d => (
                                    <span key={d} className="text-[10px] px-1.5 py-0.5 bg-green-50 text-green-700 rounded font-medium">
                                      {d}
                                    </span>
                                  ))}
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="flex space-x-1" onClick={(e) => e.stopPropagation()}>
                          <button
                            onClick={() => handleToggleActive(route)}
                            className={`p-1 rounded text-xs ${
                              route.is_active
                                ? 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                                : 'bg-green-100 text-green-700 hover:bg-green-200'
                            }`}
                            title={route.is_active ? 'Deactivate' : 'Activate'}
                          >
                            {route.is_active ? '👁️‍🗨️' : '👁️'}
                          </button>
                          <button
                            onClick={async () => {
                              try {
                                // Fetch full route data to ensure coordinates are loaded
                                const response = await axios.get(`${API}/admin/van-routes/${route.id}`, {
                                  headers: {
                                    'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
                                  }
                                });
                                const fullRoute = response.data;
                                if (fullRoute.coordinates && fullRoute.coordinates.length > 0) {
                                  setOverlayRoute({
                                    name: fullRoute.name,
                                    coordinates: fullRoute.coordinates,
                                    color: fullRoute.color
                                  });
                                  handleCreateRoute();
                                  setSuccess(`Using "${fullRoute.name}" as reference overlay.`);
                                  setTimeout(() => setSuccess(''), 3000);
                                } else {
                                  setError('Route has no coordinates');
                                }
                              } catch (err) {
                                setError('Failed to load route');
                              }
                            }}
                            className="p-1 bg-indigo-100 text-indigo-700 rounded hover:bg-indigo-200 text-xs"
                            title="Use as Reference Overlay"
                          >
                            📋
                          </button>
                          <button
                            onClick={() => handleEditRoute(route.id)}
                            className="p-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 text-xs"
                            title="Edit"
                          >
                            ✏️
                          </button>
                          <button
                            onClick={() => handleDeleteRoute(route.id)}
                            className="p-1 bg-red-100 text-red-700 rounded hover:bg-red-200 text-xs"
                            title="Delete"
                          >
                            🗑️
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right Side - Map */}
        <div
          className={`bg-white flex flex-col ${
            mapFullscreen ? 'fixed inset-0 z-[2000]' : 'rounded-lg shadow overflow-hidden'
          }`}
          style={mapFullscreen ? undefined : { height: '650px' }}
        >
          {/* Route Navigation Controls - Above Map */}
          {isCreating && isNavigating && routeForm.coordinates.length > 0 && (
            <div className="bg-green-50 border-b border-green-200 p-3 flex-shrink-0">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <span className="text-sm font-medium text-green-800">Route Navigation</span>
                  <button
                    onClick={() => {
                      const newIndex = Math.max(0, navPointIndex - 1);
                      setNavPointIndex(newIndex);
                    }}
                    disabled={navPointIndex === 0}
                    className="px-3 py-1.5 bg-green-600 text-white text-sm rounded hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                  >
                    ← Prev
                  </button>
                  <div className="flex items-center space-x-1">
                    <span className="text-xs text-gray-600">Point</span>
                    <input
                      type="number"
                      min="1"
                      max={routeForm.coordinates.length}
                      value={navPointIndex + 1}
                      onChange={(e) => {
                        const val = parseInt(e.target.value, 10);
                        if (!isNaN(val) && val >= 1 && val <= routeForm.coordinates.length) {
                          setNavPointIndex(val - 1);
                        }
                      }}
                      className="w-16 px-2 py-1 text-center text-sm border border-green-300 rounded focus:outline-none focus:ring-2 focus:ring-green-500"
                    />
                    <span className="text-xs text-gray-600">of {routeForm.coordinates.length}</span>
                  </div>
                  <button
                    onClick={() => {
                      const newIndex = Math.min(routeForm.coordinates.length - 1, navPointIndex + 1);
                      setNavPointIndex(newIndex);
                    }}
                    disabled={navPointIndex === routeForm.coordinates.length - 1}
                    className="px-3 py-1.5 bg-green-600 text-white text-sm rounded hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                  >
                    Next →
                  </button>
                </div>
                <div className="text-right">
                  <div className="text-xs text-green-700 font-mono">
                    {routeForm.coordinates[navPointIndex]?.lat.toFixed(5)}, {routeForm.coordinates[navPointIndex]?.lng.toFixed(5)}
                  </div>
                  <div className="text-xs">
                    {navPointIndex === 0 && <span className="text-green-600 font-medium">(Start - Depot)</span>}
                    {navPointIndex === routeForm.coordinates.length - 1 && <span className="text-red-600 font-medium">(End)</span>}
                    {navPointIndex > 0 && navPointIndex < routeForm.coordinates.length - 1 && <span className="text-gray-500">Waypoint</span>}
                  </div>
                </div>
              </div>
            </div>
          )}
          {/* Reference Overlay Info Bar */}
          {isCreating && overlayRoute && overlayRoute.coordinates && (
            <div className="bg-indigo-50 border-b border-indigo-200 px-4 py-2 flex-shrink-0">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <span className="text-indigo-600 text-lg">📋</span>
                  <div>
                    <span className="text-sm font-medium text-indigo-800">Reference Overlay: </span>
                    <span className="text-sm text-indigo-600">{overlayRoute.name || 'Reference Route'}</span>
                    <span className="text-xs text-indigo-500 ml-2">({overlayRoute.coordinates?.length || 0} coverage points)</span>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-xs text-indigo-600">Gray dots show streets to cover</span>
                  <button
                    onClick={() => setOverlayRoute(null)}
                    className="px-2 py-1 text-xs bg-indigo-200 text-indigo-800 rounded hover:bg-indigo-300 transition-colors"
                  >
                    ✕ Clear Overlay
                  </button>
                </div>
              </div>
            </div>
          )}
          <div className="flex-grow relative">
            <button
              onClick={() => setMapFullscreen(!mapFullscreen)}
              className={`absolute top-3 right-3 z-[1000] rounded-lg shadow-lg flex items-center justify-center font-medium hover:bg-gray-100 ${
                mapFullscreen
                  ? 'px-3 py-2 bg-white text-gray-700 text-sm'
                  : 'w-10 h-10 bg-white text-gray-700 text-xl'
              }`}
              title={mapFullscreen ? 'Exit full screen (Esc)' : 'Full screen map'}
            >
              {mapFullscreen ? '✕ Exit Full Screen' : '⛶'}
            </button>
            <MapContainer
              center={[17.2518, -88.7713]} // Belize center
              zoom={13}
              style={{ height: '100%', width: '100%' }}
            >
              <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
              <MapResizeHandler trigger={mapFullscreen} />

              {isCreating && <MapClickHandler />}

              {/* Reference Overlay - shows coverage points as visible overlay for tracing */}
              {isCreating && overlayRoute && overlayRoute.coordinates && (
                <>
                  {/* Visible polyline showing the reference route path */}
                  <Polyline
                    positions={overlayRoute.coordinates.map(coord => [coord.lat, coord.lng])}
                    color="#F97316"
                    weight={4}
                    opacity={0.7}
                    dashArray="8,12"
                  />
                  {/* Visible dots for each coverage point */}
                  {overlayRoute.coordinates.map((coord, idx) => (
                    <Marker
                      key={`overlay-${idx}`}
                      position={[coord.lat, coord.lng]}
                      icon={L.divIcon({
                        html: `<div style="
                          width: 10px;
                          height: 10px;
                          background-color: #F97316;
                          border: 2px solid #FFFFFF;
                          border-radius: 50%;
                          opacity: 0.85;
                          box-shadow: 0 1px 3px rgba(0,0,0,0.3);
                        "></div>`,
                        className: '',
                        iconSize: [10, 10],
                        iconAnchor: [5, 5]
                      })}
                      interactive={false}
                    />
                  ))}
                </>
              )}

              {/* Insert point handler for edit mode */}
              {isCreating && activeTab === 'details' && pointEditMode && (
                <InsertPointHandler
                  coordinates={routeForm.coordinates}
                  onInsertPoint={handleInsertPoint}
                  insertMode={pointEditMode}
                />
              )}

              {/* Fit map to route bounds when editing - only once on load */}
              {isCreating && routeForm.coordinates.length > 0 && (
                <MapBoundsFitter coordinates={routeForm.coordinates} fitOnce={true} />
              )}

              {/* Navigation controller - pans to current point */}
              {isCreating && isNavigating && routeForm.coordinates.length > 0 && (
                <MapNavigator
                  targetPoint={routeForm.coordinates[navPointIndex]}
                  mapRef={editMapRef}
                />
              )}

              {/* Drawing route preview (when creating/editing) */}
              {isCreating && routeForm.coordinates.length > 0 && (
                <>
                  <Polyline
                    positions={routeForm.coordinates.map(coord => [coord.lat, coord.lng])}
                    color={routeForm.color}
                    weight={pointEditMode ? 6 : 4}
                    opacity={0.7}
                  />
                  {/* Direction arrows while editing - always show */}
                  {routeForm.coordinates.length > 3 && (
                    <RouteDirectionArrows
                      coordinates={routeForm.coordinates}
                      color={routeForm.color}
                    />
                  )}
                  {/* Start/End markers while editing - always show */}
                  {routeForm.coordinates.length >= 2 && (
                    <RouteEndpointMarkers
                      coordinates={routeForm.coordinates}
                      routeName={routeForm.name || 'New Route'}
                    />
                  )}
                  {/* Draggable point markers */}
                  {routeForm.coordinates.map((coord, index) => (
                    <DraggableRouteMarker
                      key={`point-${index}-${coord.lat}-${coord.lng}`}
                      position={[coord.lat, coord.lng]}
                      index={index}
                      total={routeForm.coordinates.length}
                      color={routeForm.color}
                      onDragEnd={handlePointDragEnd}
                      onDelete={handleDeletePoint}
                      isEditMode={pointEditMode}
                    />
                  ))}
                  {/* Navigation highlight marker - shows current point being viewed */}
                  {isNavigating && routeForm.coordinates[navPointIndex] && (
                    <Marker
                      position={[routeForm.coordinates[navPointIndex].lat, routeForm.coordinates[navPointIndex].lng]}
                      icon={L.divIcon({
                        html: `
                          <div style="
                            width: 40px;
                            height: 40px;
                            background: rgba(34, 197, 94, 0.3);
                            border: 3px solid #22C55E;
                            border-radius: 50%;
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            animation: pulse 1.5s ease-in-out infinite;
                          ">
                            <div style="
                              width: 20px;
                              height: 20px;
                              background: #22C55E;
                              border-radius: 50%;
                              color: white;
                              font-size: 10px;
                              font-weight: bold;
                              display: flex;
                              align-items: center;
                              justify-content: center;
                            ">${navPointIndex + 1}</div>
                          </div>
                        `,
                        className: 'nav-highlight-marker',
                        iconSize: [40, 40],
                        iconAnchor: [20, 20]
                      })}
                      zIndexOffset={1000}
                    >
                      <Popup>
                        <div className="text-center">
                          <strong>Point {navPointIndex + 1}</strong>
                          <br />
                          <span className="text-xs text-gray-500">
                            {routeForm.coordinates[navPointIndex].lat.toFixed(5)}, {routeForm.coordinates[navPointIndex].lng.toFixed(5)}
                          </span>
                          {navPointIndex === 0 && <div className="text-green-600 font-medium mt-1">Start (Depot)</div>}
                          {navPointIndex === routeForm.coordinates.length - 1 && <div className="text-red-600 font-medium mt-1">End Point</div>}
                        </div>
                      </Popup>
                    </Marker>
                  )}
                </>
              )}

              {/* Selected route preview */}
              {!isCreating && selectedRoute && (
                <>
                  <Polyline
                    positions={selectedRoute.coordinates.map(coord => [coord.lat, coord.lng])}
                    color={selectedRoute.color}
                    weight={4}
                    opacity={0.8}
                  />
                  {/* Direction arrows */}
                  <RouteDirectionArrows
                    coordinates={selectedRoute.coordinates}
                    color={selectedRoute.color}
                  />
                  {/* Start/End markers */}
                  <RouteEndpointMarkers
                    coordinates={selectedRoute.coordinates}
                    routeName={selectedRoute.name}
                  />
                </>
              )}

              {/* Stop markers when viewing/editing stops */}
              {isCreating && activeTab === 'stops' && routeForm.stops.length > 0 && (
                <>
                  {routeForm.stops.map((stop) => (
                    <Marker
                      key={stop.id}
                      position={[stop.position.lat, stop.position.lng]}
                      icon={createStopIcon(stop.sequence, routeForm.color)}
                    >
                      <Popup>
                        <div className="text-xs">
                          <p className="font-medium text-sm">{stop.name}</p>
                          {stop.description && (
                            <p className="text-gray-600 mt-1">{stop.description}</p>
                          )}
                          <p className="text-gray-500 mt-1">Stop #{stop.sequence}</p>
                          {stop.schedule && stop.schedule.length > 0 && (
                            <div className="mt-2 pt-2 border-t border-gray-200">
                              <p className="font-medium mb-1">Schedule:</p>
                              {stop.schedule.map((sched, idx) => (
                                <div key={idx} className="text-xs bg-blue-50 px-2 py-1 rounded mb-1">
                                  {sched.time} - {sched.days.join(', ')}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </Popup>
                    </Marker>
                  ))}
                </>
              )}

              {/* Temporary stop marker when adding new stop */}
              {isCreating && activeTab === 'stops' && isAddingStop && stopForm.position && (
                <Marker
                  position={[stopForm.position.lat, stopForm.position.lng]}
                  icon={createStopIcon(stopForm.sequence, routeForm.color)}
                >
                  <Popup>
                    <div className="text-xs">
                      <p className="font-medium">New Stop Position</p>
                      <p className="text-gray-600 mt-1">
                        {stopForm.position.lat.toFixed(5)}, {stopForm.position.lng.toFixed(5)}
                      </p>
                    </div>
                  </Popup>
                </Marker>
              )}
            </MapContainer>
          </div>

          {/* Map Instructions */}
          {isCreating && (
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-white px-4 py-2 rounded-lg shadow-lg z-[1000]">
              <p className="text-sm text-gray-700">
                {activeTab === 'details' ? (
                  pointEditMode ? (
                    <>
                      <span className="font-medium text-blue-600">✏️ Edit Mode:</span>{' '}
                      Drag markers to move • Click marker for options • Click route to insert point
                    </>
                  ) : (
                    <>
                      <span className="font-medium">🖱️ Click on map</span> to add points to your route
                    </>
                  )
                ) : activeTab === 'stops' && isAddingStop ? (
                  <>
                    <span className="font-medium">📍 Click on map</span> to place stop on route
                  </>
                ) : (
                  <>
                    <span className="font-medium">🚏 View stops</span> on the map
                  </>
                )}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Auto-Generate Routes Modal */}
      {showGenerateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[2000] p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="p-6 border-b border-gray-200 flex justify-between items-center">
              <div>
                <h3 className="text-xl font-bold text-gray-900">🔄 Auto-Generate Routes from GPS History</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Analyze truck GPS data to automatically create routes by day of week
                </p>
              </div>
              <button
                onClick={handleCloseGenerateModal}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                ✕
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 overflow-y-auto flex-1">
              {generatedRoutes.length === 0 ? (
                /* Configuration View */
                <div className="space-y-6">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="font-medium text-blue-800 mb-2">How it works:</h4>
                    <ul className="text-sm text-blue-700 space-y-1">
                      <li>1. Select which days of the week to analyze</li>
                      <li>2. The system will analyze GPS history from all trucks</li>
                      <li>3. Routes are created for each truck's typical path on each day</li>
                      <li>4. Time windows are calculated based on actual traversal times</li>
                    </ul>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Select Days to Generate Routes For:
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day) => (
                        <button
                          key={day}
                          onClick={() => handleToggleGenerateDay(day)}
                          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                            selectedDays.includes(day)
                              ? 'bg-green-600 text-white'
                              : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                          }`}
                        >
                          {day}
                        </button>
                      ))}
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      Selected: {selectedDays.length} days
                    </p>
                  </div>

                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <p className="text-sm text-yellow-800">
                      <strong>Note:</strong> This will analyze GPS data from December 8, 2025 onwards.
                      Routes can be edited or deleted after generation.
                    </p>
                  </div>
                </div>
              ) : (
                /* Results View */
                <div className="space-y-4">
                  {/* Stats */}
                  {generateStats && (
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h4 className="font-medium text-gray-900 mb-2">Generation Statistics</h4>
                      <div className="grid grid-cols-3 gap-4 text-sm">
                        <div>
                          <p className="text-gray-500">Total Points Analyzed</p>
                          <p className="text-xl font-bold text-gray-900">
                            {generateStats.total_points_analyzed?.toLocaleString()}
                          </p>
                        </div>
                        <div>
                          <p className="text-gray-500">Routes Generated</p>
                          <p className="text-xl font-bold text-green-600">
                            {generateStats.routes_generated}
                          </p>
                        </div>
                        <div>
                          <p className="text-gray-500">Vehicles Analyzed</p>
                          <p className="text-xl font-bold text-gray-900">
                            {generateStats.vehicles_analyzed}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Generated Routes List */}
                  <div>
                    <h4 className="font-medium text-gray-900 mb-3">
                      Generated Routes ({generatedRoutes.length})
                    </h4>
                    <div className="space-y-2 max-h-[300px] overflow-y-auto">
                      {generatedRoutes.map((route, index) => (
                        <div
                          key={route.id}
                          className={`border rounded-lg p-3 cursor-pointer transition-colors ${
                            previewRouteIndex === index
                              ? 'border-blue-500 bg-blue-50'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                          onClick={() => setPreviewRouteIndex(previewRouteIndex === index ? null : index)}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex items-center space-x-3">
                              <div
                                className="w-4 h-4 rounded-full"
                                style={{ backgroundColor: route.color }}
                              ></div>
                              <div>
                                <p className="font-medium text-gray-900">{route.name}</p>
                                <p className="text-xs text-gray-500">
                                  {route.coordinates?.length} points •
                                  {route.schedule?.start_time} - {route.schedule?.end_time} •
                                  {route.statistics?.source_vehicle}
                                </p>
                              </div>
                            </div>
                            <span className="text-xs text-blue-600">
                              {previewRouteIndex === index ? 'Hide Preview' : 'Preview'}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Preview Map */}
                  {previewRouteIndex !== null && generatedRoutes[previewRouteIndex] && (
                    <div className="border rounded-lg overflow-hidden" style={{ height: '300px' }}>
                      <MapContainer
                        center={[
                          generatedRoutes[previewRouteIndex].statistics?.centroid?.lat || 17.2518,
                          generatedRoutes[previewRouteIndex].statistics?.centroid?.lng || -88.7713
                        ]}
                        zoom={14}
                        style={{ height: '100%', width: '100%' }}
                      >
                        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                        <Polyline
                          positions={generatedRoutes[previewRouteIndex].coordinates.map(
                            coord => [coord.lat, coord.lng]
                          )}
                          color={generatedRoutes[previewRouteIndex].color}
                          weight={4}
                          opacity={0.8}
                        />
                        {/* Direction arrows */}
                        <RouteDirectionArrows
                          coordinates={generatedRoutes[previewRouteIndex].coordinates}
                          color={generatedRoutes[previewRouteIndex].color}
                        />
                        {/* Start/End markers */}
                        <RouteEndpointMarkers
                          coordinates={generatedRoutes[previewRouteIndex].coordinates}
                          routeName={generatedRoutes[previewRouteIndex].name}
                        />
                      </MapContainer>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="p-6 border-t border-gray-200 bg-gray-50">
              {generatedRoutes.length === 0 ? (
                <div className="flex justify-end space-x-3">
                  <button
                    onClick={handleCloseGenerateModal}
                    className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleGenerateRoutes}
                    disabled={generating || selectedDays.length === 0}
                    className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                  >
                    {generating ? (
                      <>
                        <span className="animate-spin mr-2">⏳</span>
                        Analyzing GPS Data...
                      </>
                    ) : (
                      <>
                        <span className="mr-2">🔄</span>
                        Generate Routes
                      </>
                    )}
                  </button>
                </div>
              ) : (
                <div className="flex justify-between">
                  <button
                    onClick={() => {
                      setGeneratedRoutes([]);
                      setGenerateStats(null);
                      setPreviewRouteIndex(null);
                    }}
                    className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
                  >
                    ← Back to Configuration
                  </button>
                  <div className="flex space-x-3">
                    <button
                      onClick={handleCloseGenerateModal}
                      className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => handleSaveGeneratedRoutes(true)}
                      disabled={generating}
                      className="px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 disabled:opacity-50"
                      title="Replace all existing auto-generated routes"
                    >
                      Replace All & Save
                    </button>
                    <button
                      onClick={() => handleSaveGeneratedRoutes(false)}
                      disabled={generating}
                      className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 flex items-center"
                    >
                      {generating ? 'Saving...' : '💾 Save Routes'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Quadrant Routes Modal */}
      {showQuadrantModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[2000] p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="p-6 border-b border-gray-200 flex justify-between items-center">
              <div>
                <h3 className="text-xl font-bold text-gray-900">🧭 Optimized Zone Routes</h3>
                <p className="text-sm text-gray-600 mt-1">
                  {subdivideZones
                    ? 'Create 8 optimized routes (4 per zone) from all GPS history with road snapping'
                    : 'Create 2 optimized routes (North/South) from all GPS history with road snapping'
                  }
                </p>
              </div>
              <button
                onClick={handleCloseQuadrantModal}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                ✕
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 overflow-y-auto flex-1">
              {quadrantRoutes.length === 0 ? (
                /* Configuration View */
                <div className="space-y-6">
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                    <h4 className="font-medium text-purple-800 mb-2">How it works:</h4>
                    <ul className="text-sm text-purple-700 space-y-1">
                      <li>1. <strong>Amalgamates ALL GPS history</strong> to build complete street coverage</li>
                      <li>2. <strong>Divides into {subdivideZones ? '8 quadrants' : '2 zones'}</strong> {subdivideZones ? '(4 per zone)' : '(North and South)'} from depot</li>
                      <li>3. <strong>Optimizes each route</strong> for minimal backtracking</li>
                      <li>4. <strong>Snaps to actual roads</strong> using OSRM for realistic paths</li>
                      <li>5. <strong>Auto-assigns schedules</strong> {subdivideZones ? '(2 quadrants per day, Mon-Thu)' : '(North: Mon/Wed/Fri, South: Tue/Thu/Sat)'}</li>
                    </ul>
                  </div>

                  {/* Mode Selection */}
                  <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                    <h4 className="font-medium text-indigo-800 mb-3">Route Mode:</h4>
                    <div className="flex items-center space-x-6">
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="radio"
                          name="zoneMode"
                          checked={!subdivideZones}
                          onChange={() => setSubdivideZones(false)}
                          className="h-4 w-4 text-indigo-600"
                        />
                        <span className="text-sm text-gray-700">
                          <strong>2 Zones</strong> - North & South (simpler)
                        </span>
                      </label>
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="radio"
                          name="zoneMode"
                          checked={subdivideZones}
                          onChange={() => setSubdivideZones(true)}
                          className="h-4 w-4 text-indigo-600"
                        />
                        <span className="text-sm text-gray-700">
                          <strong>8 Quadrants</strong> - Subdivide each zone into 4 (more granular)
                        </span>
                      </label>
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 mb-3">Expected Output:</h4>
                    {!subdivideZones ? (
                      /* 2-Zone Mode */
                      <div className="grid grid-cols-2 gap-4 text-center">
                        <div className="bg-blue-100 rounded-lg p-4">
                          <div className="text-3xl mb-2">🔵</div>
                          <p className="font-medium text-blue-800 text-lg">North Zone</p>
                          <p className="text-sm text-gray-600">Mon, Wed, Fri</p>
                        </div>
                        <div className="bg-amber-100 rounded-lg p-4">
                          <div className="text-3xl mb-2">🟡</div>
                          <p className="font-medium text-amber-800 text-lg">South Zone</p>
                          <p className="text-sm text-gray-600">Tue, Thu, Sat</p>
                        </div>
                      </div>
                    ) : (
                      /* 8-Quadrant Mode */
                      <div className="space-y-4">
                        <div>
                          <p className="text-sm font-medium text-gray-700 mb-2">North Zone (4 quadrants):</p>
                          <div className="grid grid-cols-4 gap-2 text-center">
                            <div className="bg-blue-100 rounded-lg p-2">
                              <div className="text-lg">🔵</div>
                              <p className="font-medium text-blue-800 text-xs">North-NW</p>
                              <p className="text-xs text-gray-500">Mon</p>
                            </div>
                            <div className="bg-blue-50 rounded-lg p-2">
                              <div className="text-lg">🔵</div>
                              <p className="font-medium text-blue-700 text-xs">North-NE</p>
                              <p className="text-xs text-gray-500">Mon</p>
                            </div>
                            <div className="bg-blue-200 rounded-lg p-2">
                              <div className="text-lg">🔵</div>
                              <p className="font-medium text-blue-900 text-xs">North-SW</p>
                              <p className="text-xs text-gray-500">Tue</p>
                            </div>
                            <div className="bg-blue-50 rounded-lg p-2">
                              <div className="text-lg">🔵</div>
                              <p className="font-medium text-blue-600 text-xs">North-SE</p>
                              <p className="text-xs text-gray-500">Tue</p>
                            </div>
                          </div>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-700 mb-2">South Zone (4 quadrants):</p>
                          <div className="grid grid-cols-4 gap-2 text-center">
                            <div className="bg-amber-100 rounded-lg p-2">
                              <div className="text-lg">🟡</div>
                              <p className="font-medium text-amber-800 text-xs">South-NW</p>
                              <p className="text-xs text-gray-500">Wed</p>
                            </div>
                            <div className="bg-yellow-100 rounded-lg p-2">
                              <div className="text-lg">🟡</div>
                              <p className="font-medium text-yellow-700 text-xs">South-NE</p>
                              <p className="text-xs text-gray-500">Wed</p>
                            </div>
                            <div className="bg-amber-200 rounded-lg p-2">
                              <div className="text-lg">🟡</div>
                              <p className="font-medium text-amber-900 text-xs">South-SW</p>
                              <p className="text-xs text-gray-500">Thu</p>
                            </div>
                            <div className="bg-yellow-50 rounded-lg p-2">
                              <div className="text-lg">🟡</div>
                              <p className="font-medium text-yellow-600 text-xs">South-SE</p>
                              <p className="text-xs text-gray-500">Thu</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      id="roadSnapping"
                      checked={useRoadSnapping}
                      onChange={(e) => setUseRoadSnapping(e.target.checked)}
                      className="h-4 w-4 text-purple-600 rounded"
                    />
                    <label htmlFor="roadSnapping" className="text-sm text-gray-700">
                      <strong>Use Road Snapping (OSRM)</strong> - Creates routes that follow actual roads (recommended)
                    </label>
                  </div>

                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <p className="text-sm text-yellow-800">
                      <strong>Note:</strong> This process may take {subdivideZones ? '2-4' : '1-2'} minutes as it analyzes 500,000+ GPS points
                      and calculates optimal road-following paths. Existing quadrant routes will be replaced.
                      {subdivideZones && ' You can rename quadrants and change schedules after saving.'}
                    </p>
                  </div>
                </div>
              ) : (
                /* Results View */
                <div className="space-y-4">
                  {/* Stats */}
                  {quadrantStats && (
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h4 className="font-medium text-gray-900 mb-2">Generation Statistics</h4>
                      <div className="grid grid-cols-4 gap-4 text-sm mb-4">
                        <div>
                          <p className="text-gray-500">Routes Generated</p>
                          <p className="text-xl font-bold text-green-600">
                            {quadrantStats.routes_generated}
                          </p>
                        </div>
                        <div>
                          <p className="text-gray-500">Total Miles</p>
                          <p className="text-xl font-bold text-purple-600">
                            {quadrantStats.total_route_miles?.toFixed(1)}
                          </p>
                        </div>
                        <div>
                          <p className="text-gray-500">Avg per Route</p>
                          <p className="text-xl font-bold text-gray-900">
                            {quadrantStats.routes_generated > 0
                              ? (quadrantStats.total_route_miles / quadrantStats.routes_generated).toFixed(1)
                              : 0} mi
                          </p>
                        </div>
                        <div>
                          <p className="text-gray-500">Street Cells</p>
                          <p className="text-xl font-bold text-blue-600">
                            {quadrantStats.merged_total_cells?.toLocaleString()}
                          </p>
                        </div>
                      </div>
                      {/* OSM Integration Info */}
                      <div className="bg-purple-50 rounded p-3 text-xs">
                        <p className="font-medium text-purple-800 mb-1">OSM Integration:</p>
                        <div className="grid grid-cols-3 gap-2 text-purple-700">
                          <span>OSM Streets: {quadrantStats.osm_street_cells?.toLocaleString()}</span>
                          <span>GPS Points Used: {quadrantStats.gps_points_in_boundary?.toLocaleString()}</span>
                          <span>Outside Boundary: {quadrantStats.gps_points_filtered_out?.toLocaleString()}</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Quadrant Routes Grid */}
                  <div>
                    <h4 className="font-medium text-gray-900 mb-3">
                      Generated {quadrantRoutes.length > 2 ? 'Quadrant' : 'Zone'} Routes ({quadrantRoutes.length})
                    </h4>
                    <div className={`grid gap-3 ${quadrantRoutes.length > 4 ? 'grid-cols-2 md:grid-cols-4' : 'grid-cols-2'}`}>
                      {quadrantRoutes.map((route, index) => (
                        <div
                          key={route.id}
                          className={`border rounded-lg ${quadrantRoutes.length > 4 ? 'p-2' : 'p-4'} transition-colors ${
                            previewQuadrantIndex === index
                              ? 'border-purple-500 bg-purple-50'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <div className={`flex items-start ${quadrantRoutes.length > 4 ? 'flex-col space-y-2' : 'justify-between'}`}>
                            <div
                              className={`flex items-center ${quadrantRoutes.length > 4 ? 'space-x-2' : 'space-x-3'} cursor-pointer flex-1`}
                              onClick={() => setPreviewQuadrantIndex(previewQuadrantIndex === index ? null : index)}
                            >
                              <div
                                className={`${quadrantRoutes.length > 4 ? 'w-4 h-4' : 'w-6 h-6'} rounded-full flex-shrink-0`}
                                style={{ backgroundColor: route.color }}
                              ></div>
                              <div>
                                <p className={`font-medium text-gray-900 ${quadrantRoutes.length > 4 ? 'text-sm' : ''}`}>{route.name}</p>
                                <p className="text-xs text-gray-500">
                                  {route.statistics?.distance_miles?.toFixed(1)} mi
                                  {quadrantRoutes.length <= 4 && ` • ${route.coordinates?.length} pts • ${route.statistics?.street_segments} segs`}
                                </p>
                                <p className="text-xs text-purple-600">
                                  {route.schedule?.days?.join(', ')}
                                </p>
                              </div>
                            </div>
                            <div className={`flex ${quadrantRoutes.length > 4 ? 'flex-row space-x-2 w-full' : 'flex-col items-end space-y-2'}`}>
                              <span
                                className={`text-xs text-purple-600 cursor-pointer hover:underline ${quadrantRoutes.length > 4 ? '' : ''}`}
                                onClick={() => setPreviewQuadrantIndex(previewQuadrantIndex === index ? null : index)}
                              >
                                {previewQuadrantIndex === index ? 'Hide' : 'Preview'}
                              </span>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (route.coordinates && route.coordinates.length > 0) {
                                    setOverlayRoute({
                                      name: route.name,
                                      coordinates: route.coordinates,
                                      color: route.color
                                    });
                                    setShowQuadrantModal(false);
                                    handleCreateRoute();
                                    setSuccess(`Using "${route.name}" as reference overlay. Draw your optimized route on top.`);
                                    setTimeout(() => setSuccess(''), 5000);
                                  } else {
                                    setError('Route has no coordinates');
                                  }
                                }}
                                className="px-2 py-1 text-xs bg-indigo-100 text-indigo-700 rounded hover:bg-indigo-200 transition-colors"
                              >
                                📋 Use as Reference
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Preview Map */}
                  {previewQuadrantIndex !== null && quadrantRoutes[previewQuadrantIndex] && (
                    <div className="border rounded-lg overflow-hidden" style={{ height: '350px' }}>
                      <MapContainer
                        center={[17.2518, -88.7713]}
                        zoom={13}
                        style={{ height: '100%', width: '100%' }}
                      >
                        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                        {/* Show all routes faded */}
                        {quadrantRoutes.map((route, idx) => (
                          <React.Fragment key={route.id}>
                            <Polyline
                              positions={route.coordinates.map(coord => [coord.lat, coord.lng])}
                              color={route.color}
                              weight={idx === previewQuadrantIndex ? 5 : 2}
                              opacity={idx === previewQuadrantIndex ? 0.9 : 0.25}
                            />
                            {/* Direction arrows for selected route */}
                            {idx === previewQuadrantIndex && (
                              <>
                                <RouteDirectionArrows
                                  coordinates={route.coordinates}
                                  color={route.color}
                                />
                                <RouteEndpointMarkers
                                  coordinates={route.coordinates}
                                  routeName={route.name}
                                />
                              </>
                            )}
                          </React.Fragment>
                        ))}
                        {/* Depot marker */}
                        <Marker position={[17.251749, -88.767876]}>
                          <Popup>
                            <div className="text-sm font-medium">Belmopan City Council</div>
                            <div className="text-xs text-gray-500">Depot / Start Point</div>
                          </Popup>
                        </Marker>
                      </MapContainer>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="p-6 border-t border-gray-200 bg-gray-50">
              {quadrantRoutes.length === 0 ? (
                <div className="flex justify-end space-x-3">
                  <button
                    onClick={handleCloseQuadrantModal}
                    className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleGenerateQuadrantRoutes}
                    disabled={generatingQuadrants}
                    className="px-6 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                  >
                    {generatingQuadrants ? (
                      <>
                        <span className="animate-spin mr-2">⏳</span>
                        Generating Optimized Routes...
                      </>
                    ) : (
                      <>
                        <span className="mr-2">🧭</span>
                        Generate Quadrant Routes
                      </>
                    )}
                  </button>
                </div>
              ) : (
                <div className="flex justify-between">
                  <button
                    onClick={() => {
                      setQuadrantRoutes([]);
                      setQuadrantStats(null);
                      setPreviewQuadrantIndex(null);
                    }}
                    className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
                  >
                    ← Back to Configuration
                  </button>
                  <div className="flex space-x-3">
                    <button
                      onClick={handleCloseQuadrantModal}
                      className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSaveQuadrantRoutes}
                      disabled={generatingQuadrants}
                      className="px-6 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50 flex items-center"
                    >
                      {generatingQuadrants ? 'Saving...' : '💾 Save & Replace Routes'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VanRouteManagement;
